/*
 * Supreme Phone Parts: "More <model> parts" block (sections/spp-model-parts.liquid).
 *
 * 1. Liquid detects the device model of the current product (snippets/spp-mp-model) and renders
 *    the block with placeholder cards at its final height, plus a JSON config.
 * 2. As soon as the page is parsed, this script makes ONE request to the Section Rendering API:
 *    a product search for the model phrases, rendered by sections/spp-model-parts-feed.liquid
 *    as JSON.
 * 3. Results are filtered on the exact model ("iPhone 17 Pro" never shows "17 Pro Max" parts,
 *    "Galaxy A16" never shows "A16 5G" parts), sorted by part type and rendered as cards with
 *    quick add, replacing the placeholders at the same height. With no other parts the block
 *    collapses at once (display: none).
 *
 * The pure matching helpers are exposed on window.SppModelParts so they can be tested.
 */
(function (win) {
  'use strict';
  if (win.SppModelParts) return;

  /* ---------------------------------------------------------------- matching */

  const FAMILIES = ['iphone', 'ipad', 'ipod', 'galaxy', 'pixel', 'macbook'];
  const SUBBRANDS = ['redmi', 'poco', 'moto', 'xperia'];
  // Bases whose "5G" phone is a different device (model-mapper's models.json: both names exist).
  // Any other trailing 5G is ignored (Galaxy S21 5G = Galaxy S21), and 4G is always ignored.
  const SIBLINGS_5G = [
    'galaxy a06', 'galaxy a07', 'galaxy a13', 'galaxy a14', 'galaxy a15', 'galaxy a16', 'galaxy a17', 'galaxy a22',
    'galaxy a23', 'galaxy a32', 'galaxy a51', 'galaxy a52', 'galaxy a71', 'galaxy a72', 'galaxy m32', 'galaxy s10',
    'galaxy s20', 'galaxy s20 plus', 'galaxy s20 fe', 'galaxy note 10 plus', 'galaxy note 20', 'galaxy note 20 ultra',
    'honor x8', 'huawei p40 lite', 'oppo a53', 'oppo a54', 'oppo a57', 'oppo a72', 'oppo a74', 'oppo reno5',
    'pixel 4a', 'realme 7', 'realme 8', 'redmi 12', 'redmi note 10', 'redmi note 12 pro'
  ];
  // A model match must not run on into a longer model name.
  const STOP = [
    '[\\w+]',
    '\\.\\d',
    ' (?:Pro|Max|Plus|Mini|Ultra|Lite|FE|Edge|Fold|Flip|XL|Neo|Fusion|Compact|Play|Core|SE|Premium|Performance|Smart|Go|Air|Life|Prime|GT|Tablet|UW|E|X)(?![\\w-])',
    ' (?:II|III|IV|V|VI|VII|VIII)(?![\\w-])',
    ' Power(?! (?:and|&|Buttons?|Flex|Key|Volume|On|Off|On/Off|Switch|Meter|Input|Adapter|Management|Cable|IC)(?!\\w))(?!\\w)',
    ' NFC(?! (?:Antenna|Coil|Flex|Chip|Module|Cable|Sticker|Wireless|Charging|Charger|Reader|Board|Tag|Sensor|and|&|with)(?!\\w))(?!\\w)',
    ' \\(?\\d{1,2}(?:st|nd|rd|th) Gen'
  ];
  const YEAR_STOP = ' (?:19|20)\\d\\d(?!\\w)';
  const B = '(?:^|[^a-z0-9_])';

  const reEsc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const words = (s) => String(s).split(' ').filter(Boolean);
  // "Galaxy S24+" -> "Galaxy S24 Plus", 'iPad mini 5 (2019)' -> "iPad mini 5 2019", drops inch marks.
  const norm = (s) => String(s)
    .replace(/[″“”"]/g, ' ')
    .replace(/[()]/g, ' ')
    .replace(/\bAmazon Kindle Fire\b/gi, 'Amazon Fire')
    .replace(/([a-z0-9])\+(?=$|[\s/,&|])/gi, '$1 Plus')
    .replace(/\s+/g, ' ')
    .trim();

  function parseModel(model) {
    const w = words(norm(model));
    const lw = w.map((x) => x.toLowerCase());
    const i = lw.findIndex((x) => FAMILIES.includes(x));
    let fam;
    let famWord;
    let famPat;
    let rest;
    if (i >= 0) {
      fam = lw[i];
      famWord = w[i];
      famPat = fam === 'galaxy' ? '(?:Galaxy|Samsung)' : reEsc(w[i]);
      rest = w.slice(i + 1);
    } else if (w.length > 2 && SUBBRANDS.includes(lw[1])) {
      fam = lw[1];
      famWord = w[1];
      famPat = '(?:' + reEsc(w[0]) + ' )?' + reEsc(w[1]);
      rest = w.slice(2);
    } else {
      fam = lw[0] || '';
      famWord = w[0] || '';
      famPat = reEsc(famWord);
      rest = w.slice(1);
    }
    // Only a trailing year and a trailing 4G/5G are suffixes: "Moto G 5G Plus" keeps its 5G.
    const core = rest.slice();
    let year = '';
    let g5 = false;
    if (core.length > 1 && /^(19|20)\d\d$/.test(core[core.length - 1])) year = core.pop();
    if (core.length > 1 && /^[45]g$/i.test(core[core.length - 1])) g5 = core.pop().toLowerCase() === '5g';
    if (!year && core.length > 1 && /^(19|20)\d\d$/.test(core[core.length - 1])) year = core.pop();
    const coreStr = core.join(' ');
    // "Moto G 5G": with a one-letter core the 5G is part of the phone's name (model-mapper).
    const sig5 = SIBLINGS_5G.includes((fam + ' ' + coreStr).toLowerCase()) || (core.length === 1 && /^[a-z]$/i.test(core[0]));
    if (!sig5) g5 = false;
    // Series word: the core up to its first digit token ("16" for 16 Plus, "Note 20" for Note 20 Ultra).
    let series = '';
    const d = core.findIndex((x) => (/\d/.test(x) && !/^[45]g$/i.test(x) && !/^\d+(st|nd|rd|th)$/i.test(x)) || (fam === 'iphone' && /^(x|xs|xr|se)$/i.test(x)));
    if (d >= 0 && fam !== 'ipad' && !/\b(fe|edge|lite|neo|tab)\b/i.test(coreStr)) series = core.slice(0, d + 1).join(' ');
    return { model: String(model), fam, famWord, famPat, core: coreStr, year, g5, sig5, series, tab: /\btab\b/i.test(coreStr) };
  }

  // Comparable key for tags: "Samsung Galaxy A16 4G" and "Galaxy A16" give "galaxy a16".
  function modelKey(s, m) {
    let w = words(norm(s).toLowerCase());
    if (w.length > 2 && (FAMILIES.includes(w[1]) || SUBBRANDS.includes(w[1]))) w = w.slice(1);
    w = w.filter((x) => x !== '4g' && !(x === '5g' && !m.sig5));
    return w.join(' ');
  }

  /* iPad generations: titles say "iPad 10.2 (2019)", "iPad 7th Gen" or "iPad 7" for one device. */
  const IPAD = {
    base: { '9.7/2017': 5, '9.7/2018': 6, '10.2/2019': 7, '10.2/2020': 8, '10.2/2021': 9, '10.9/2022': 10, '11/2025': 11 },
    mini: { '7.9/2019': 5, '8.3/2021': 6, '8.3/2024': 7 },
    air: { '9.7/2013': 1, '9.7/2014': 2, '10.5/2019': 3, '10.9/2020': 4, '10.9/2022': 5 },
    'pro12.9': { 2015: 1, 2017: 2, 2018: 3, 2020: 4, 2021: 5, 2022: 6 },
    pro11: { 2018: 1, 2020: 2, 2021: 3, 2022: 4 }
  };
  const ORD = (n) => n + (n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th');

  // One iPad mention ("iPad 10.2 2019", "mini 5", "8th Gen") -> "line:gen", using ctx for line/size.
  function ipadKey(segment, ctx) {
    const t = ' ' + norm(segment).toLowerCase() + ' ';
    const c = Object.assign({ line: 'base', size: '' }, ctx || {});
    if (/\bipad\b/.test(t)) { c.line = 'base'; c.size = ''; }
    if (/\bmini\b/.test(t)) c.line = 'mini';
    else if (/\bair\b/.test(t)) c.line = 'air';
    else if (/\bpro\b/.test(t)) c.line = 'pro';
    const size = /\b(7\.9|8\.3|9\.7|10\.2|10\.5|10\.9|11|12\.9)\b(?!\d)/.exec(t.replace(/\b(19|20)\d\d\b/g, ' '));
    if (size) c.size = size[1];
    const year = (/\b((?:19|20)\d\d)\b/.exec(t) || [])[1] || '';
    let gen = Number((/\b(\d{1,2})(?:st|nd|rd|th)\b/.exec(t) || [])[1] || 0);
    if (!gen && c.line !== 'pro') {
      const bare = /\b(?:ipad|mini|air)\s+(\d{1,2})\b(?![.\d])/.exec(t);
      if (bare && !(c.line === 'base' && bare[1] === '11' && year)) gen = Number(bare[1]);
    }
    const line = c.line === 'pro' ? 'pro' + (c.size || '') : c.line;
    if (!gen && IPAD[line]) gen = IPAD[line][(line.startsWith('pro') ? '' : c.size + '/') + year] || 0;
    if (!gen && (line === 'pro10.5' || line === 'pro9.7')) gen = 1;
    return { key: gen ? line + ':' + gen : '', ctx: c, gen, line, size: c.size, year };
  }

  // Every iPad generation a title names: "iPad 10.2 7th & 8th Gen" -> base:7, base:8.
  function ipadKeys(title) {
    const keys = [];
    let ctx = null;
    String(title).split('|')[0].split(/\s*(?:\/|&|,|\band\b)\s*/i).forEach((seg) => {
      if (!seg.trim()) return;
      if (!ctx && !/\bipad\b/i.test(seg)) return;
      const k = ipadKey(seg, ctx);
      ctx = k.ctx;
      if (k.key) keys.push(k.key);
    });
    return keys;
  }

  // Other ways titles name the same iPad, used for the search query.
  function ipadPhrases(model) {
    const k = ipadKey(model);
    if (!k.key) return [];
    const L = k.line === 'base' ? 'iPad' : k.line === 'mini' ? 'iPad mini' : k.line === 'air' ? 'iPad Air' : 'iPad Pro' + (k.size ? ' ' + k.size : '');
    const out = [L + ' ' + ORD(k.gen) + ' Gen'];
    if (k.line !== 'pro' && !k.line.startsWith('pro')) out.push(L + ' ' + k.gen);
    if (k.size && !k.line.startsWith('pro')) out.push(L + ' ' + k.size);
    return out;
  }

  // Samsung model numbers "SM-A166B" -> "A166" (the region letter after the digits is ignored).
  const smCodes = (t) => new Set((String(t).match(/\bSM-?\s?[A-Z]\d{3}/gi) || []).map((c) => c.replace(/^SM-?\s?/i, '').toUpperCase()));
  const shares = (a, b) => [...a].some((x) => b.has(x));

  function makeMatcher(m) {
    const stops = STOP.slice();
    // Models without a number (iPhone XS, iPhone SE) must not run into "SE 2" or "SE 3rd Gen",
    // but a capacity such as "XS 2659mAh" is fine.
    if (!/\d/.test(m.core)) stops.push(' \\d{1,2}(?:st|nd|rd|th)?(?![\\d.])(?![a-z])');
    // "Huawei P8 Lite" is not "P8 Lite 2017". An iPad title without the year still fits
    // (iPad mini 5 = 2019), but iPad Pro sizes span generations, so they need the year.
    const yearOptional = m.year && m.fam === 'ipad' && !/^pro\b|\./i.test(m.core);
    stops.push(YEAR_STOP);
    // An ignored 5G suffix must not hide a longer name: "Moto G 5G Plus" is not "Moto G 5G".
    if (!m.sig5) stops.push(' [45]G' + STOP[2]);
    // "Moto G8 Power" is not "Moto G8 Power and Volume Button Flex".
    if (/\bpower$/i.test(m.core)) stops.push(' (?:and|&|Buttons?|Flex|Keys?|Volume|On|Off|Switch|Cable|IC)(?!\\w)');
    // Without the 5G twin rule: used to spot titles that name the base with no 4G/5G at all.
    const tailBase = '(?! ?[45]G(?!\\w)|' + stops.join('|') + ')';
    if (m.sig5 && !m.g5) stops.push(' 5G(?!\\w)');
    const tail = '(?!' + stops.join('|') + ')';
    const yearPart = m.year ? (yearOptional ? '(?: ' + m.year + ')?' : ' ' + m.year) : '';
    const coreRe = words(m.core).map(reEsc).join(' ');
    const body = coreRe + yearPart + '(?: 4G)?' + (m.g5 ? ' 5G' : '');
    const reFull = new RegExp(B + m.famPat + ' ' + body + tail, 'i');
    const reFam = new RegExp(B + m.famPat + '(?![a-z0-9])', 'i');
    // Multi-model titles: "iPhone 16 / 16 Plus", "XS and XS Max", "Galaxy S24 / S24+", "| Fits 11 Pro Max".
    const reAlt = new RegExp('(?:[/,&|]|\\b(?:and|fits|for)\\b) ?(?:' + m.famPat + ' )?' + body + tail, 'i');
    // Space-separated model lists: "Clear Case for Samsung Galaxy A16 A17 A26", "| S22+ S22 Ultra M52 A73".
    const reList = new RegExp('(?:' + B + m.famPat + '|\\|)(?: [a-z]{0,2}\\d{1,3}[a-z]?(?: (?:Plus|Ultra|Pro|Lite|FE))?)+ ' + body + tail, 'i');
    const listOk = /^[a-z]+\d/i.test(m.core);
    // Family titles: "iPhone 12 / 12 Pro & iPhone 16 Series" fits every iPhone 16 model.
    const reSeries = m.series ? new RegExp(B + words(m.series).map(reEsc).join(' ') + ' Series(?!\\w)', 'i') : null;
    // A 5G model whose title only names the base ("Galaxy A15 OLED | SM-A155F SM-A156B") still counts
    // when the product is tagged with this exact model.
    const reBase = m.g5 ? new RegExp(B + m.famPat + ' ' + coreRe + yearPart + '(?: 4G)?' + tail, 'i') : null;
    const otherTab = m.fam === 'galaxy' && !m.tab ? /\bGalaxy Tab\b/i : null;
    const ipad = m.fam === 'ipad' ? ipadKey(m.model).key : '';
    const key = modelKey(m.model, m);
    const tagged = (tags) => !!(tags && tags.some((tag) => modelKey(tag, m) === key));
    const match = function (title, tags) {
      const t = norm(title);
      if (reFull.test(t)) return true;
      if (ipad && ipadKeys(title).includes(ipad)) return true;
      if (otherTab && otherTab.test(t)) return false;
      // The title names a model of this family but not this one: trust the title, not the tag.
      if (reFam.test(t)) {
        return reAlt.test(t) || (listOk && reList.test(t)) || !!(reSeries && reSeries.test(t)) || !!(reBase && reBase.test(t) && tagged(tags));
      }
      return tagged(tags);
    };
    match.namesFamily = (title) => reFam.test(norm(title));
    // The title names this model's base with no 4G/5G after it (Samsung twin models only).
    const reBaseOnly = new RegExp(B + m.famPat + ' ' + coreRe + yearPart + tailBase, 'i');
    match.namesBase = m.sig5 ? (title) => reBaseOnly.test(norm(title)) : () => false;
    // Names this family without saying which model: "Galaxy A16" on an A16 5G page, "iPad 10.2 inch".
    match.vague = (title) => match.namesBase(title) || (!!ipad && !ipadKeys(title).length);
    // The model a variant names, for counting distinct models ("Galaxy S20+ (Dual)" -> "s20 plus").
    const reLabel = new RegExp(B + m.famPat + ' ([a-z]*\\d+[a-z]*)((?: (?:plus|ultra|pro|max|lite|fe|mini|edge|[45]g)){0,2})', 'i');
    match.label = (title) => { const h = reLabel.exec(norm(title)); return (h ? h[1] + h[2] : norm(title)).toLowerCase(); };
    match.twin = !!m.sig5;
    return match;
  }

  // Search phrases joined with OR, for one Section Rendering API request.
  function buildQuery(m) {
    const out = [];
    const add = (s, quote) => {
      s = String(s || '').replace(/"/g, '').replace(/\s+/g, ' ').trim();
      if (!s) return;
      s = quote === false ? s : '"' + s + '"';
      if (!out.includes(s)) out.push(s);
    };
    const g5 = m.g5 ? ' 5G' : '';
    // "Galaxy S24 Plus" is written "S24+" in titles: search the base phrase, filter later.
    const core = m.core.replace(/ Plus\b/gi, '');
    add(m.famWord + ' ' + core + (core === m.core ? g5 : ''));
    if (/[a-z]/i.test(core) && core.length > 1) add(core + (core === m.core ? g5 : ''));
    if (m.series) add(m.series + ' Series');
    // Bare code too ("S24"): phrase OR queries have dropped a listing ("Galaxy S24 / S24+ SIM Tray").
    const code = core.split(' ')[0];
    if (/^[a-z]+\d+[a-z]?$/i.test(code)) add(code, false);
    if (m.fam === 'ipad') ipadPhrases(m.model).forEach((p) => add(p));
    return out.join(' OR ');
  }

  // "View all" search that lists only this model: one title phrase, minus the longer model names
  // the feed shows ("iPhone 17 Pro" -> -title:"iPhone 17 Pro Max", "Galaxy A16" -> -title:"Galaxy A16 5G").
  // Only model words are excluded, never part words. One phrase only: OR-ed title phrases match
  // their words anywhere in the title ("iPad 3" OR ... finds "iPad mini 3").
  const SUFFIX_WORD = /^(?:pro|max|plus|mini|ultra|lite|fe|edge|fold|flip|xl|neo|air|e|se|x|z|5g|play|power|prime|life|core|gt|go|smart|compact|premium|performance|fusion|turbo|star|tablet|ii|iii|iv|v|vi)$/i;
  // Words as the store search compares them: no "+" or punctuation ("S24+" = "S24"), and a plural
  // or trailing letter dropped from model codes ("10S" = "10", "A20e" = "A20s" = "A20").
  const stem = (w) => (/\d/.test(w) ? w.replace(/^([a-z]*\d+)s$/, '$1').replace(/^([a-z]*\d+)e$/, (x, a) => (x.length >= 4 ? a : x)) : w);
  const clean = (s) => String(s).replace(/[^A-Za-z0-9.]+/g, ' ').replace(/\s+/g, ' ').trim();
  const searchText = (s) => ' ' + clean(s).toLowerCase().split(' ').map(stem).join(' ') + ' ';
  const hasPhrase = (title, p) => searchText(title).includes(searchText(p));
  function viewAll(m, rawPhrase, items, fitting) {
    const phrase = String(rawPhrase).replace(/"/g, '').trim();
    const base = clean(phrase);
    const P = searchText(phrase).trim().split(' ');
    const own = (fitting || []).map((x) => searchText(x.t));
    const neg = [];
    (items || []).forEach((it) => {
      if (!it || neg.length >= 6) return;
      const raw = clean(it.t).split(' ');
      const st = raw.map((x) => stem(x.toLowerCase()));
      for (let i = 0; i + P.length < st.length; i += 1) {
        if (!P.every((w, j) => st[i + j] === w)) continue;
        const w = raw[i + P.length];
        const next = (raw[i + P.length + 1] || '').toLowerCase();
        const year = /^(?:19|20)\d\d$/.test(w);
        // A 5G page searched by its base name ("Galaxy S20+") leaves out the 4G twin instead.
        const twin4 = m.g5 && /^4g$/i.test(w) && !/\b5G$/i.test(phrase);
        if (!twin4 && !SUFFIX_WORD.test(w) && !(year && !m.year)) continue;
        // "Power" names a model ("Moto G9 Power"), not "Power and Volume Button Flex".
        if (/^power$/i.test(w) && /^(and|buttons?|flex|keys?|volume|on|off|switch|cable|ic)$/.test(next)) continue;
        if (m.g5 && /^5g$/i.test(w)) continue;
        const ext = base + ' ' + w;
        // A year is left out only when none of this model's own parts carries it.
        if (year && own.some((t) => t.includes(searchText(ext)))) continue;
        if (!neg.some((x) => x.toLowerCase() === ext.toLowerCase()) && neg.length < 6) neg.push(ext);
      }
    });
    const q = 'title:"' + phrase + '"' + neg.map((x) => ' -title:"' + x + '"').join('');
    return { q, phrase, neg, has: (t) => hasPhrase(t, phrase) && !neg.some((x) => hasPhrase(t, x)) };
  }
  const exactQuery = (m, phrase, items) => viewAll(m, phrase, items).q;

  // iPads are named by generation, size or year: "iPad 7th Gen", "iPad 7", "iPad 10.2 7th", "iPad 10.2 (2019)".
  function ipadNames(model) {
    const k = ipadKey(model);
    if (!k.key) return [];
    const table = IPAD[k.line] || {};
    const known = Object.keys(table).find((x) => table[x] === k.gen) || '';
    const size = k.size || (known.includes('/') ? known.split('/')[0] : '');
    const year = k.year || (known.includes('/') ? known.split('/')[1] : known);
    const pro = k.line.startsWith('pro');
    const L = k.line === 'base' ? 'iPad' : k.line === 'mini' ? 'iPad mini' : k.line === 'air' ? 'iPad Air' : 'iPad Pro' + (size ? ' ' + size : '');
    const out = [L + ' ' + ORD(k.gen) + ' Gen'];
    if (!pro) out.push(L + ' ' + k.gen);
    if (size && !pro) out.push(L + ' ' + size + ' ' + ORD(k.gen));
    if (year) out.push(L + (size && !pro ? ' ' + size : '') + ' ' + year);
    return out;
  }

  // The "View all" query for a rendered block, or '' when no search can list this model alone and
  // the block already shows every part. Tries the page phrase, the base name of a Samsung 5G twin
  // whose parts rarely say "5G" (S20+ 5G), the model without its brand ("Amazon Fire HD 7" parts
  // say "Kindle Fire HD 7") and iPad generation names. Replays each search on the feed items and
  // picks the one that lists no other model and the most of this model's parts.
  function viewAllLink(chosen, pageModel, phrase, pid, items) {
    const cm = chosen.model || pageModel;
    const fitting = chosen.all || chosen;
    const cands = [phrase];
    if (cm.g5 && cm.sig5 && /\s5G$/i.test(phrase)) cands.push(phrase.replace(/\s+5G$/i, ''));
    const brandless = phrase.split(' ').slice(1).join(' ');
    if (!FAMILIES.includes(cm.fam) && !SUBBRANDS.includes(cm.fam) && /\d/.test(brandless)) cands.push(brandless);
    if (cm.fam === 'ipad') ipadNames(cm.model).forEach((p) => cands.push(p));
    else if (cm.year && !phrase.includes(cm.year)) cands.push(phrase + ' ' + cm.year);
    const fits = new Set(fitting.map((p) => p.id));
    fits.add(pid);
    let best = null;
    cands.forEach((p) => {
      const spec = viewAll(cm, p, items, fitting);
      const strays = (items || []).some((it) => it && !fits.has(it.id) && spec.has(it.t));
      const score = fitting.filter((x) => spec.has(x.t)).length - (strays ? 1000 : 0);
      if (!best || score > best.score) best = { spec, strays, score };
    });
    // Search ignores "+", so "S24" also finds "S24+": no link rather than a mixed list.
    return best.strays ? '' : best.spec.q;
  }

  // Part type: [rank, label]. Order follows the brief: screens, batteries, back glass and
  // housing, cameras, charging port, then the rest.
  function classify(title) {
    const t = ' ' + String(title).split('|')[0].toLowerCase() + ' ';
    const has = (re) => re.test(t);
    if (has(/\b(clear case|phone case|silicone case|case cover|screen protector|tempered glass|privacy glass)\b/)) return [10, 'Accessory'];
    if (has(/\b(back|rear) (glass|cover|panel|door)\b|\bbattery (cover|door)\b|\bback ?glass\b/)) return [3, 'Back glass'];
    if (has(/\b(housing|chassis|middle frame|mid-?frame)\b/)) return [3, 'Housing'];
    if (has(/\b(fpc|lcd flex|display flex|lcd connector|display connector)\b/)) return [7.5, 'Flex cable'];
    if (has(/\b(screen|lcd|oled|amoled|display|digiti[sz]er|touch panel)\b/)) return [1, 'Screen'];
    if (has(/\bbatter(y|ies)\b/)) return [2, 'Battery'];
    if (has(/\b(camera lens|lens glass|camera glass|lens cover|lens ring|lens protector)\b/)) return [4.2, 'Camera lens'];
    if (has(/\b(front|selfie) camera\b/)) return [4.1, 'Front camera'];
    if (has(/\bcamera\b/)) return [4, 'Rear camera'];
    if (has(/\b(charging (port|board|flex|connector)|charger port|dock connector|usb-?c? (port|board)|type-c (port|board))\b/)) return [5, 'Charging port'];
    if (has(/\b(loud ?speaker|ringer|buzzer)\b/)) return [6, 'Loudspeaker'];
    if (has(/\b(earpiece|ear speaker|call speaker)\b/)) return [6.1, 'Earpiece speaker'];
    if (has(/\bspeaker\b/)) return [6.2, 'Speaker'];
    if (has(/\b(microphone|mic)\b/)) return [6.3, 'Microphone'];
    if (has(/\bcoil\b/)) return [7.2, has(/\b(wireless|charging)\b/) ? 'Charging coil' : 'NFC coil'];
    if (has(/\b(power|volume|side key|home button|buttons?)\b/)) return [7, 'Buttons'];
    if (has(/\bsim\b/)) return [8, has(/\btray\b/) ? 'SIM tray' : 'SIM reader'];
    if (has(/\b(vibrat\w*|taptic|haptic)\b/)) return [9, 'Vibration motor'];
    if (has(/\bantenna\b/)) return [7.6, 'Antenna'];
    if (has(/\b(flex|connector|cable)\b/)) return [7.5, 'Flex cable'];
    return [11, 'Part'];
  }

  // Card name: the title without brand, model and "Replacement" ("Rear Camera Lens Glass").
  // Only strips the model when it is the exact model ("iPhone 13 mini ..." stays whole on iPhone 13).
  function cardName(title, m) {
    const s = String(title).split('|')[0].replace(/\s+/g, ' ').trim();
    const core = words(m.core).map((w) => (/^plus$/i.test(w) ? '\\s?(?:Plus|\\+)' : ' ' + reEsc(w))).join('').replace(/^ /, '');
    const lead = new RegExp('^(?:(?:For|Apple|Samsung|Google|Genuine|Original|New) )*' + m.famPat + ' ' + core +
      '(?: \\(?(?:19|20)\\d\\d\\)?)?(?: [45]G)?(?=\\s|$)(?!' + STOP.slice(2).join('|') + ')', 'i');
    let name = s;
    const hit = lead.exec(s);
    if (hit) {
      const remainder = s.slice(hit[0].length).trim();
      if (remainder && !/^([/,&+]|and\b|or\b)/i.test(remainder)) name = remainder;
    }
    if (name === s) name = s.replace(/^(?:(?:For|Apple|Samsung|Google) )+/i, '');
    name = name.replace(/\breplacement\b/gi, ' ').replace(/\s+/g, ' ').replace(/^[-–\s]+|[-–\s]+$/g, '').trim();
    return name || s;
  }

  // Card name for a part that fits through one of its variants: the title's model list says other
  // models ("Galaxy A25 / A15 5G / A55 / A35 TFT LCD Screen" on an S20 Ultra page), so keep only
  // the part words ("TFT LCD Screen"). The full title and the variant stay in the screen-reader text.
  const MODEL_TOKEN = '(?:[A-Z]{0,2}\\d{1,3}(?:\\.\\d)?[A-Z]?|Air|SE|XR|XS|X|Mini)(?: (?:Plus|Ultra|Pro|Max|Lite|FE|[45]G|Mini|Series|Edge|Fold|Flip))*\\+?';
  const LEAD_MODELS = new RegExp('^(?:(?:For|Apple|Samsung|Google|Xiaomi|Motorola|Sony|Huawei|Honor|OPPO|OnePlus|Nokia|Realme|Vivo|Genuine|Original|New) )*(?:(?:Galaxy(?: Tab)?|iPhone|iPad(?: Pro| Air| mini)?|Pixel|Redmi(?: Note)?|Moto|Xperia) )?(?:' +
    MODEL_TOKEN + '(?:\\s*(?:/|,|&|and)\\s*(?:(?:Galaxy|iPhone|iPad|Pixel) )?|\\s+))+', 'i');
  const TAIL_MODELS = new RegExp('\\s+for\\s+(?:(?:Samsung|Apple|Google) )?(?:(?:Galaxy|iPhone|iPad|Pixel) )?(?:' + MODEL_TOKEN + '[\\s/,&]*)+$', 'i');
  function partName(title) {
    const s = String(title).split('|')[0].replace(/\s+/g, ' ').trim();
    const rest = s.replace(LEAD_MODELS, '').replace(TAIL_MODELS, '').replace(/\breplacement\b/gi, ' ').replace(/\s+/g, ' ').trim();
    return rest.length >= 3 ? rest.charAt(0).toUpperCase() + rest.slice(1) : '';
  }

  // Variant options often name the exact model ("Model: iPhone 17 Pro Max"). The matching variant
  // becomes the card's link, price and quick-add variant. Products whose variants all name other
  // models of the family are left out.
  function pickVariant(it, match) {
    const vs = (it.vo || []).filter((v) => v && v[2]);
    if (vs.length < 2) return { hits: [], others: false };
    const hits = [];
    const labels = new Set();
    let family = 0;
    vs.forEach((v) => {
      if (match(v[2], null)) hits.push(v);
      else if (match.namesFamily(v[2]) && !match.vague(v[2])) { family += 1; labels.add(match.label(v[2])); }
    });
    hits.sort((a, b) => Number(!!b[1]) - Number(!!a[1]));
    // Variants that list other models leave the product out. When they all repeat one model
    // ("For Galaxy S20+ (Dual) / Blue" on "S20+ / S20 Ultra SIM Tray"), the title still decides,
    // except for 4G/5G twins, where such variants say which twin a vague title is for.
    const others = !hits.length && family === vs.length && (labels.size >= 2 || match.twin);
    return { hits, others };
  }

  // Samsung 4G/5G twins (A16 / A16 5G, S20+ / S20+ 5G): titles often omit "5G" but list model
  // numbers. Decide with the current product's SM- numbers when it has any:
  // - a title that names the base without 4G/5G counts when it shares an SM- number with the
  //   current product, and is left out when it lists only other SM- numbers;
  // - a base page whose SM- numbers all belong to "5G" listings becomes the 5G model.
  function samsungTwins(items, m, title) {
    const cur = smCodes(title);
    if (!m.sig5 || !cur.size) return { m, extra: () => false, drop: () => false };
    const base = String(m.model).replace(/\s+[45]G\b/gi, '');
    const m5 = m.g5 ? m : parseModel(base + ' 5G');
    const mBase = m.g5 ? parseModel(base) : m;
    if (!m5.g5) return { m, extra: () => false, drop: () => false };
    const f5 = makeMatcher(m5);
    const fBase = makeMatcher(mBase);
    // SM- numbers that the store's own titles tie to one twin ("A16 5G ... SM-A166B", "A16 4G ... SM-A165F").
    const re4 = new RegExp(B + mBase.famPat + ' ' + words(mBase.core).map(reEsc).join(' ') + ' 4G(?!\\w)', 'i');
    const learned5 = new Set();
    const learned4 = new Set();
    items.forEach((it) => {
      if (!it) return;
      if (f5(it.t, null) && !fBase(it.t, null)) smCodes(it.t).forEach((c) => learned5.add(c));
      else if (re4.test(norm(it.t)) && !f5(it.t, null)) smCodes(it.t).forEach((c) => learned4.add(c));
    });
    let page = m;
    if (!m.g5 && [...cur].every((c) => learned5.has(c))) page = m5;
    const other = page.g5 ? learned4 : learned5;
    // Names the base in any form ("S20+ ...", "S20 / S20+ ...") and never says 5G for it.
    const ambiguous = (t) => fBase(t, null) && !f5(t, null);
    // Numbers of the same line as this product ("G986" and "G981" are S20 numbers, "A175" is not A16).
    const sameLine = (t) => [...smCodes(t)].filter((x) => [...cur].some((y) => x.slice(0, 3) === y.slice(0, 3)));
    return {
      m: page,
      extra: (t) => page.g5 && ambiguous(t) && shares(smCodes(t), cur),
      // Left out only when its numbers of this line are all known numbers of the other twin, so
      // "A13 4G ... SM-A135F" stays on an SM-A137F page, and "A16 ... SM-A166B" leaves A16 4G pages.
      drop: (t) => { const c = sameLine(t); return ambiguous(t) && c.length > 0 && !c.some((x) => cur.has(x)) && c.every((x) => other.has(x)); }
    };
  }

  // Titles that declare several models: "Choose Model", "Series", "and 12 More Models", or three
  // or more model codes ("A17 A27 A37 ..."; SM- numbers are not counted).
  const MULTI_WORDS = /\b(?:choose model|series|more models?|\d+ models|all models)\b/i;
  const multiModel = (t) => MULTI_WORDS.test(t) ||
    (String(t).replace(/\bSM-?\s?[A-Z]\d{3}[A-Z]{0,3}\b/gi, ' ').match(/\b[A-Z]{1,2}\d{1,3}[A-Z]?\b/gi) || []).length >= 3;

  function select(items, model, opts) {
    let m = model;
    const twins = samsungTwins(items || [], m, opts.title || '');
    m = twins.m;
    const match = makeMatcher(m);
    const seen = new Set();
    const out = [];
    (items || []).forEach((it) => {
      if (!it || !it.id || seen.has(it.id) || it.id === opts.pid) return;
      seen.add(it.id);
      let ok = match(it.t, it.tg) || twins.extra(it.t);
      if (ok && twins.drop(it.t)) ok = false;
      const pv = pickVariant(it, match);
      // Multi-model listings ("Choose Model", "Series", "12 More Models") fit every model their
      // variants name.
      let viaVariant = false;
      if (!ok && pv.hits.length && multiModel(it.t) && match.namesFamily(it.t)) { ok = true; viaVariant = true; }
      if (!ok || pv.others) return;
      if (pv.hits.length) {
        // One matching variant: quick add it. Several (colours): link to the first, buyer chooses.
        const v = pv.hits[0];
        const one = pv.hits.length === 1;
        it = Object.assign({}, it, {
          v: v[0], a: pv.hits.some((x) => x[1]), p: v[3] || it.p, i: v[4] || it.i, vc: one ? 1 : pv.hits.length,
          pv: !one && pv.hits.some((x) => x[3] !== v[3]), vt: one ? v[2] : '', u: it.u + '?variant=' + v[0]
        });
      }
      const c = classify(it.t);
      const name = (viaVariant && partName(it.t)) || cardName(it.t, m);
      out.push(Object.assign({}, it, { rank: c[0], type: c[1].toLowerCase() === name.toLowerCase() ? '' : c[1], name }));
    });
    out.sort((a, b) => (Number(b.a) - Number(a.a)) || (a.rank - b.rank) || a.name.localeCompare(b.name));
    const list = out.slice(0, opts.max || 20);
    list.model = m;
    list.all = out;
    return list;
  }

  const api = { norm, parseModel, makeMatcher, modelKey, buildQuery, exactQuery, viewAll, viewAllLink, classify, cardName, partName, select, ipadKeys, smCodes };
  win.SppModelParts = api;
  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- block */

  const escHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function cardHtml(p) {
    const full = String(p.t).split('|')[0].trim() + (p.vt ? ' (' + p.vt + ')' : '');
    const img = p.i
      ? '<img src="' + escHtml(p.i) + '" srcset="' + escHtml(p.i.replace(/width=\d+/, 'width=180')) + ' 180w, ' + escHtml(p.i) + ' 360w" sizes="(min-width: 1100px) 250px, (min-width: 750px) 22vw, 44vw" alt="" loading="lazy" decoding="async" width="360" height="360">'
      : '';
    const badge = p.a ? '' : '<span class="sppm-badge">Sold out</span>';
    let action;
    if (!p.a) action = '<span class="sppm-out">Sold out</span>';
    else if (p.vc === 1 && p.v) action = '<button type="button" class="sppm-add" data-sppm-add="' + Number(p.v) + '" aria-label="Add ' + escHtml(full) + ' to cart">Add</button>';
    else action = '<a class="sppm-opt" href="' + escHtml(p.u) + '" aria-label="Choose options for ' + escHtml(full) + '">Choose</a>';
    return '<li class="sppm-card' + (p.a ? '' : ' is-soldout') + '" data-handle="' + escHtml(p.h) + '" data-type="' + escHtml(p.type) + '">' +
      '<a class="sppm-link" href="' + escHtml(p.u) + '">' +
        '<span class="sppm-img">' + img + badge + '</span>' +
        '<span class="sppm-sr">' + escHtml(full) + '</span>' +
        '<span class="sppm-type" aria-hidden="true">' + escHtml(p.type) + '</span>' +
        '<span class="sppm-name" aria-hidden="true">' + escHtml(p.name) + '</span>' +
      '</a>' +
      '<div class="sppm-foot">' +
        '<span class="sppm-price">' + escHtml(p.p) + '<small>' + (p.pv ? 'From, inc. VAT' : 'inc. VAT') + '</small></span>' +
        action +
      '</div>' +
    '</li>';
  }

  function parseFeed(html) {
    const hit = /<script[^>]*data-sppm-feed[^>]*>([\s\S]*?)<\/script>/.exec(html);
    if (!hit) throw new Error('feed missing');
    return JSON.parse(hit[1]);
  }

  function init(root) {
    if (!root || root.__sppm) return;
    const cfgEl = root.querySelector('script[data-sppm-config]');
    if (!cfgEl) return;
    root.__sppm = true;
    let cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    const m = parseModel(cfg.model);
    const wrap = root.closest('[data-sppm-wrap]') || root;
    const list = root.querySelector('[data-sppm-list]');
    const status = root.querySelector('[data-sppm-status]');
    const count = root.querySelector('[data-sppm-count]');
    const all = root.querySelector('[data-sppm-all]');
    const prev = root.querySelector('[data-sppm-prev]');
    const next = root.querySelector('[data-sppm-next]');
    const ac = new AbortController();
    const opt = { signal: ac.signal };
    const params = new URLSearchParams({ q: buildQuery(m), type: 'product', 'options[unavailable_products]': 'last', section_id: cfg.feed });
    const feedUrl = (cfg.search || '/search') + '?' + params.toString();
    root.setAttribute('data-sppm-feed-url', feedUrl);
    const design = !!(win.Shopify && win.Shopify.designMode);

    const setState = (s) => root.setAttribute('data-sppm-state', s);
    const show = () => { root.removeAttribute('aria-busy'); wrap.hidden = false; };

    function hide() {
      setState('empty');
      root.removeAttribute('aria-busy');
      if (design) {
        list.innerHTML = '<li class="sppm-empty">No other ' + escHtml(cfg.display || cfg.model) + ' parts yet. This block stays hidden on the live store.</li>';
        wrap.hidden = false;
        return;
      }
      wrap.hidden = true;
    }

    function updateNav() {
      if (!prev || !next) return;
      const over = list.scrollWidth > list.clientWidth + 2;
      prev.hidden = next.hidden = !over;
      prev.disabled = list.scrollLeft <= 2;
      next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
    }

    function render(chosen, items) {
      if (!chosen.length) { hide(); return; }
      // A Samsung page refined to its 5G model (by SM- number) keeps its heading text: changing it
      // after load could wrap the heading and shift the block.
      const cm5 = !!(chosen.model && chosen.model.g5 && !m.g5);
      list.innerHTML = chosen.map(cardHtml).join('');
      const inStock = chosen.filter((p) => p.a).length;
      root.setAttribute('data-sppm-items', String(chosen.length));
      // The count's line is reserved in the markup (a non-breaking space), so filling it shifts nothing.
      if (count && inStock) {
        count.innerHTML = '<span class="sppm-sep" aria-hidden="true"> · </span><i class="sppm-dot" aria-hidden="true"></i>' + inStock + ' in stock';
        count.removeAttribute('aria-hidden');
      }
      if (all) {
        // Product pages: only when there are more parts than the block shows. Articles
        // ("all": "exact"): whenever the search lists this model alone.
        const more = cfg.all === 'exact' || (chosen.all || chosen).length > chosen.length;
        const link = more ? viewAllLink(chosen, m, (cm5 ? cfg.phrase + ' 5G' : cfg.phrase) || cfg.model, Number(cfg.pid), items) : '';
        if (!link) {
          all.hidden = true;
        } else {
          all.hidden = false;
          all.href = (cfg.search || '/search') + '?' + new URLSearchParams({ q: link, type: 'product' }).toString();
          const sr = all.querySelector('.sppm-sr');
          if (sr && cm5 && cfg.display) sr.textContent = ' ' + cfg.display + ' 5G parts';
        }
      }
      setState('ready');
      show();
      updateNav();
    }

    async function load() {
      if (root.__sppmLoading) return;
      root.__sppmLoading = true;
      setState('loading');
      try {
        let items = [];
        let page = 1;
        let pages = 1;
        do {
          const r = await fetch(feedUrl + (page > 1 ? '&page=' + page : ''), { signal: ac.signal, credentials: 'same-origin' });
          if (!r.ok) throw new Error('HTTP ' + r.status);
          const feed = parseFeed(await r.text());
          items = items.concat(feed.items || []);
          pages = Number(feed.pages) || 1;
          page += 1;
        } while (page <= pages && page <= 4);
        if (/[?&]sppm_test=soldout\b/.test(location.search) && items.length) {
          // QA fixture only: shows the sold-out card state with live data that has no sold-out items.
          items = items.map((it, i) => (i === 0 ? Object.assign({}, it, { a: false, vo: [] }) : it));
        }
        render(select(items, m, { pid: Number(cfg.pid), max: Number(cfg.max) || 20, title: cfg.title || '' }), items);
      } catch (e) {
        if (e.name !== 'AbortError') { setState('error'); hide(); }
      }
    }

    async function add(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      const T = win.theme || {};
      const routes = T.routes || {};
      const name = (btn.getAttribute('aria-label') || '').replace(/^Add /, '').replace(/ to cart$/, '');
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      btn.setAttribute('aria-busy', 'true');
      status.textContent = '';
      try {
        const res = await fetch(routes.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-sppm-add')), quantity: 1 }], sections, sections_url: location.pathname }),
          signal: ac.signal
        }).then((r) => r.json());
        const cart = await fetch((routes.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' }, signal: ac.signal }).then((r) => r.json());
        const pub = T.pubsub && T.pubsub.PUB_SUB_EVENTS;
        if (res.status) {
          status.textContent = res.description || res.message || 'Could not add ' + name + ' to the cart.';
          return;
        }
        if (T.settings && T.settings.cartType === 'page') { location.href = routes.cart_url || '/cart'; return; }
        const sectionsOk = res.sections && Object.values(res.sections).every(Boolean);
        if (sectionsOk) cart.sections = res.sections;
        status.textContent = 'Added ' + name + ' to the cart. Cart now has ' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's') + '.';
        if (pub) T.pubsub.publish(pub.cartUpdate, { source: 'spp-model-parts', cart });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: res } }));
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          if (!sectionsOk) document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
          else drawer.show(btn);
        } else if (!pub) {
          location.href = routes.cart_url || '/cart';
        }
      } catch (e) {
        if (e.name !== 'AbortError') status.textContent = 'Something went wrong. Please try again.';
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-sppm-add]');
      if (btn) { add(btn); return; }
      const nav = e.target.closest('[data-sppm-prev], [data-sppm-next]');
      if (nav) {
        const dir = nav.hasAttribute('data-sppm-next') ? 1 : -1;
        const smooth = !win.matchMedia('(prefers-reduced-motion: reduce)').matches;
        list.scrollBy({ left: dir * list.clientWidth * 0.9, behavior: smooth ? 'smooth' : 'auto' });
      }
    }, opt);
    list.addEventListener('scroll', () => win.requestAnimationFrame(updateNav), { passive: true, signal: ac.signal });
    win.addEventListener('resize', () => win.requestAnimationFrame(updateNav), { passive: true, signal: ac.signal });
    // Keep a keyboard-focused card fully visible in the scroll row.
    list.addEventListener('focusin', (e) => {
      const c = e.target.closest('.sppm-card');
      if (!c || list.scrollWidth <= list.clientWidth) return;
      const lr = list.getBoundingClientRect();
      const cr = c.getBoundingClientRect();
      if (cr.left < lr.left) list.scrollLeft += cr.left - lr.left - 2;
      else if (cr.right > lr.right) list.scrollLeft += cr.right - lr.right + 2;
    }, opt);

    // The block takes no space until it has cards, so load straight away: the cards are then in
    // place long before a visitor can scroll down to them (and before scroll restore on reload).
    // One request per page.
    load();
    root.__sppmTeardown = () => ac.abort();
  }

  const initAll = (scope) => (scope || document).querySelectorAll('[data-sppm]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-sppm]').forEach((r) => r.__sppmTeardown && r.__sppmTeardown());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
})(typeof window !== 'undefined' ? window : globalThis);
